import { escapeHtml } from "./format";

/**
 * Configuration options and allowlist rules for the HTML sanitizer.
 */
export interface IHtmlSanitizerPolicy {
  /** Permitted HTML tag names in lowercase (e.g. ['p', 'b', 'i', 'a', 'contact']). */
  allowedTags: string[];
  /** Permitted attribute names per tag in lowercase (e.g. { a: ['href', 'title'] }). */
  allowedAttributes: Record<string, string[]>;
  /** Globally permitted URL protocols/schemes (e.g. ['http', 'https', 'mailto']). */
  allowedSchemes: string[];
  /** Per-tag permitted URL schemes (e.g. { a: ['http', 'https', 'mailto'] }). */
  allowedSchemesByTag: Record<string, string[]>;
  /** Completely prohibited tags whose inner content must be excised entirely. */
  forbiddenContentTags: string[];
  /** Whether to enforce target="_blank" and rel="noopener noreferrer" on anchor links. */
  enforceSecureLinks: boolean;
}

/**
 * Standard default HTML allowlist policy for Memories descriptions and rich text fields.
 */
export const MEMORIES_HTML_POLICY: IHtmlSanitizerPolicy = {
  allowedTags: [
    // Headings & Text Structure
    "h1",
    "h2",
    "h3",
    "h4",
    "h5",
    "h6",
    "p",
    "br",
    "blockquote",
    "pre",
    "code",
    // Inline Formatting
    "b",
    "strong",
    "i",
    "em",
    "u",
    "s",
    "strike",
    "span",
    "a",
    // Lists
    "ul",
    "ol",
    "li",
    // Tables
    "table",
    "thead",
    "tbody",
    "tfoot",
    "tr",
    "th",
    "td",
    // Platform Domain Custom Entity Mentions
    "contact",
    "group",
    "media",
    "memory",
  ],
  allowedAttributes: {
    a: ["href", "title", "target", "rel"],
    span: ["data-font", "data-size"],
    media: ["id", "size", "width", "height", "align"],
    contact: ["id"],
    group: ["id"],
    memory: ["id"],
  },
  allowedSchemes: ["http", "https", "mailto"],
  allowedSchemesByTag: {
    a: ["http", "https", "mailto"],
  },
  forbiddenContentTags: [
    "script",
    "style",
    "iframe",
    "object",
    "embed",
    "applet",
    "meta",
    "link",
    "base",
    "form",
    "svg",
    "noscript",
    "textarea",
    "input",
    "button",
    "select",
    "option",
  ],
  enforceSecureLinks: true,
};

/**
 * Validates whether a given URL is safe and uses an authorized protocol scheme.
 * Strictly disallows 'javascript:', 'vbscript:', 'data:', or control characters.
 *
 * @param {string} url - The URL string to inspect.
 * @param {string[]} [allowedSchemes] - Permitted schemes (defaults to ['http', 'https', 'mailto']).
 * @returns {boolean} True if the URL is safe for navigation and embedding.
 */
export function isSafeUrl(url: string, allowedSchemes: string[] = MEMORIES_HTML_POLICY.allowedSchemes): boolean {
  if (!url || typeof url !== "string") return false;

  const trimmed = url.trim();
  if (!trimmed) return false;

  // Allow relative URLs starting with / or #
  if (trimmed.startsWith("/") || trimmed.startsWith("#")) {
    return true;
  }

  // Detect and reject control characters, null bytes, and leading whitespace
  const sanitized = trimmed.replace(/[\u0000-\u001f\u007f-\u009f]/g, "");

  // Match URL scheme: e.g. "https:", "javascript:"
  const schemeMatch = sanitized.match(/^([a-zA-Z0-9+.-]+):/);
  if (!schemeMatch) {
    // Relative path without leading slash
    return !sanitized.includes(":");
  }

  const scheme = schemeMatch[1].toLowerCase();
  return allowedSchemes.includes(scheme);
}

/**
 * Unescapes standard HTML entities back to raw text.
 *
 * @param {string} html - Escaped HTML string.
 * @returns {string} Unescaped raw string.
 */
export function unescapeHtml(html: string): string {
  if (!html) return "";
  return html
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#039;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&nbsp;/g, " ");
}

/**
 * Strips all HTML tags and unescapes standard entities to produce plain text.
 *
 * @param {string} str - Raw or tagged HTML string.
 * @returns {string} Plain text string.
 */
export function stripHtml(str: string): string {
  if (!str) return "";
  return unescapeHtml(str.replace(/<[^>]*>/g, ""));
}

/**
 * Sanitizes input HTML string against a configurable allow-list policy.
 * Defends against XSS, DOM clobbering, CSS injection, and phishing redirects.
 *
 * @param {string} rawHtml - Unsanitized HTML string.
 * @param {Partial<IHtmlSanitizerPolicy>} [customPolicy] - Optional custom policy overrides.
 * @returns {string} Sanitized, safe HTML string.
 */
export function sanitizeHtml(rawHtml: string, customPolicy?: Partial<IHtmlSanitizerPolicy>): string {
  if (!rawHtml || typeof rawHtml !== "string") return "";

  const policy: IHtmlSanitizerPolicy = {
    ...MEMORIES_HTML_POLICY,
    ...customPolicy,
    allowedTags: (customPolicy?.allowedTags || MEMORIES_HTML_POLICY.allowedTags).map((t) => t.toLowerCase()),
    allowedSchemes: (customPolicy?.allowedSchemes || MEMORIES_HTML_POLICY.allowedSchemes).map((s) => s.toLowerCase()),
    forbiddenContentTags: (customPolicy?.forbiddenContentTags || MEMORIES_HTML_POLICY.forbiddenContentTags).map((t) =>
      t.toLowerCase()
    ),
  };

  const allowedTagsSet = new Set(policy.allowedTags);
  let sanitized = rawHtml;

  // 1. Remove dangerous tags and all their inner content
  for (const tag of policy.forbiddenContentTags) {
    const tagRegex = new RegExp(`<${tag}\\b[^<]*(?:(?!<\\/${tag}>)<[^<]*)*<\\/${tag}>`, "gi");
    sanitized = sanitized.replace(tagRegex, "");
    const loneTagRegex = new RegExp(`<\\/?${tag}\\b[^>]*>`, "gi");
    sanitized = sanitized.replace(loneTagRegex, "");
  }

  // 2. Strip all inline `style="..."` or `style='...'` attributes
  sanitized = sanitized.replace(/\sstyle\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");

  // 3. Strip all inline `on[event]=...` handlers (e.g. onclick, onload, onerror)
  sanitized = sanitized.replace(/\son[a-zA-Z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "");

  // 4. Strip dangerous URI schemes from attributes
  sanitized = sanitized.replace(/(href|src)\s*=\s*(?:"javascript:[^"]*"|'javascript:[^']*')/gi, "");
  sanitized = sanitized.replace(/(href|src)\s*=\s*(?:"vbscript:[^"]*"|'vbscript:[^']*')/gi, "");
  sanitized = sanitized.replace(/(href|src)\s*=\s*(?:"data:[^"]*"|'data:[^']*')/gi, "");

  // 5. Filter HTML tags against the ALLOWED_TAGS whitelist
  sanitized = sanitized.replace(/<\/?([a-zA-Z0-9_-]+)(\s+[^>]*)?>/gi, (match, tagName: string, attributes: string = "") => {
    const lowerTag = tagName.toLowerCase();
    const isClosing = match.startsWith("</");

    if (!allowedTagsSet.has(lowerTag)) {
      // Disallowed tag: strip tag wrapper but preserve inner text
      return "";
    }

    if (isClosing) {
      return `</${lowerTag}>`;
    }

    // For allowed tags, filter permitted attributes
    let safeAttrs = "";

    // Entity Chips (<contact>, <group>, <media>, <memory>)
    if (lowerTag === "contact" || lowerTag === "group" || lowerTag === "media" || lowerTag === "memory") {
      const idMatch = attributes.match(/\bid\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const idVal = idMatch ? idMatch[1] ?? idMatch[2] ?? idMatch[3] ?? "" : "";

      if (lowerTag === "media") {
        const sizeMatch = attributes.match(/\bsize\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
        const sizeVal = sizeMatch ? sizeMatch[1] ?? sizeMatch[2] ?? sizeMatch[3] ?? "" : "";
        const sizeAttr = sizeVal ? ` size="${escapeHtml(sizeVal)}"` : "";

        const widthMatch = attributes.match(/\bwidth\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
        const widthVal = widthMatch ? widthMatch[1] ?? widthMatch[2] ?? widthMatch[3] ?? "" : "";
        const widthAttr = widthVal ? ` width="${escapeHtml(widthVal)}"` : "";

        const heightMatch = attributes.match(/\bheight\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
        const heightVal = heightMatch ? heightMatch[1] ?? heightMatch[2] ?? heightMatch[3] ?? "" : "";
        const heightAttr = heightVal ? ` height="${escapeHtml(heightVal)}"` : "";

        const alignMatch = attributes.match(/\balign\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
        const alignVal = alignMatch ? alignMatch[1] ?? alignMatch[2] ?? alignMatch[3] ?? "" : "";
        const alignAttr = alignVal ? ` align="${escapeHtml(alignVal)}"` : "";

        return `<media id="${escapeHtml(idVal)}"${sizeAttr}${widthAttr}${heightAttr}${alignAttr}>`;
      }

      return `<${lowerTag} id="${escapeHtml(idVal)}">`;
    }

    // Anchor tags (<a>)
    if (lowerTag === "a") {
      const hrefMatch = attributes.match(/\bhref\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const hrefVal = hrefMatch ? hrefMatch[1] ?? hrefMatch[2] ?? hrefMatch[3] ?? "" : "";
      const titleMatch = attributes.match(/\btitle\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      const titleVal = titleMatch ? titleMatch[1] ?? titleMatch[2] ?? titleMatch[3] ?? "" : "";

      const allowedSchemesForLink = policy.allowedSchemesByTag?.a || policy.allowedSchemes;
      if (isSafeUrl(hrefVal, allowedSchemesForLink)) {
        safeAttrs += ` href="${escapeHtml(hrefVal)}"`;
        if (policy.enforceSecureLinks) {
          safeAttrs += ` target="_blank" rel="noopener noreferrer"`;
        }
        if (titleVal) {
          safeAttrs += ` title="${escapeHtml(titleVal)}"`;
        }
      }
    }

    // Span tags (<span>)
    if (lowerTag === "span") {
      const fontMatch = attributes.match(/\bdata-font\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      if (fontMatch) {
        const fontVal = fontMatch[1] ?? fontMatch[2] ?? fontMatch[3] ?? "";
        safeAttrs += ` data-font="${escapeHtml(fontVal)}"`;
      }
      const sizeMatch = attributes.match(/\bdata-size\s*=\s*(?:"([^"]*)"|'([^']*)'|([^\s>]+))/i);
      if (sizeMatch) {
        const sizeVal = sizeMatch[1] ?? sizeMatch[2] ?? sizeMatch[3] ?? "";
        safeAttrs += ` data-size="${escapeHtml(sizeVal)}"`;
      }
    }

    return `<${lowerTag}${safeAttrs}>`;
  });

  return sanitized.trim();
}
